/* eslint-disable react/require-default-props */
import React, { useEffect, useState, useRef, FC, MouseEvent } from 'react';
import jwtDecode from 'jwt-decode';
import { useParams } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { Icons } from '@sede-enterprise/shell-ds-react-framework';
import { toast } from 'react-toastify';
import { HubConnectionBuilder } from '@microsoft/signalr';
import Toast from './style';
import {
  getNominations,
  nominationsActions,
} from '../../store/reducers/Nominations';
import { sendNotification } from '../../UI/Notification';
import { COMMON_MESSAGES, GET_SIGNALR_TOKEN } from '../../constants/Common';
import { IFullState } from '../../types/All.type';
import { get } from '../GenesisApi';
import {
  dashboardActions,
  getNominationStatus,
} from '../../store/reducers/Dashboard';
import {
  notificationActions,
  getActiveNotifications,
} from '../../store/Notifications';

const { IconCircleInfo } = Icons;

interface IToastComp {
  message: string;
  onRefresh: (event: MouseEvent<HTMLButtonElement>) => void;
}

interface IMessage {
  MessageDate: string;
  Message: string;
}

interface TokenPayload {
  exp: number;
  [key: string]: string | number;
}

type INotificationSubCategory = 'TimerNotification' | 'UIChangeNotification';

type INominationSubCategory =
  | 'SyncNomination'
  | 'RejectedNomination'
  | 'StatusNomination';

type IHealthCheckSubCategory = 'GeminiHealthCheck';

interface IBroadcast {
  Category: 'Nomination' | 'Notification' | 'HealthCheck';
  SubCategory:
    | INotificationSubCategory
    | INominationSubCategory
    | IHealthCheckSubCategory;
  MessageDetailsList: IMessage[];
  Message: string | string[];
  GasDay: string;
  Count: number;
  Status: {
    NominationStatus: boolean;
    EntryCapacityStatus: boolean;
    ExitCapacityStatus: boolean;
    TradeMatchingStatus: boolean;
    SchedulingStatus: boolean;
  };
  IsHealthy?: boolean;
}

const ToastComp: FC<IToastComp> = ({ message, onRefresh = () => {} }) => (
  <Toast className='info-toast'>
    <div className='content'>
      <IconCircleInfo width={25} height={25} />
      <span>{message}</span>
    </div>
    <div className='actions'>
      <button type='button' onClick={onRefresh}>
        Refresh
      </button>
    </div>
  </Toast>
);

const SignalR = () => {
  const dispatch = useDispatch();
  const { tabName = '' } = useParams();
  const [connection, setConnection] = useState<any>(null);
  const [url, setUrl] = useState<string | undefined>();
  const [token, setToken] = useState<string | undefined>();
  const renewTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const { gasDay } = useSelector((state: IFullState) => state.Nominations);

  // Create a ref to store the current value of tabName & no
  const stateRef = useRef<{
    tabName: string;
    nominationsGasDay: string;
  }>({
    tabName,
    nominationsGasDay: gasDay,
  });

  // Helper to fetch and set the token
  const fetchToken = () => {
    const NUMBER_5 = 5;
    const FIVE_MINUTES = NUMBER_5 * 60; // seconds
    get(GET_SIGNALR_TOKEN)
      .then((response) => {
        const accessToken = response.data?.accessToken;
        const signalRURL = response.data?.url;
        setUrl(signalRURL);
        setToken(accessToken);

        // Set up renewal timer
        if (accessToken) {
          try {
            const { exp } = jwtDecode<TokenPayload>(accessToken);
            const now = Math.floor(Date.now() / 1000);
            const secondsUntilRenew = Math.max(exp - now - FIVE_MINUTES, 10); // minimum 10s before expiry
            if (renewTimer.current) {
              clearTimeout(renewTimer.current);
            }
            renewTimer.current = setTimeout(
              fetchToken,
              secondsUntilRenew * 1000
            );
          } catch {
            // If decode fails, just renew in 25 minutes as fallback
            if (renewTimer.current) {
              clearTimeout(renewTimer.current);
            }
            const NUMBER_25 = 25;
            renewTimer.current = setTimeout(fetchToken, NUMBER_25 * 60 * 1000);
          }
        }
      })
      .catch(() => {
        sendNotification({
          type: 'error',
          message: COMMON_MESSAGES.ERROR,
        });
      });
  };

  useEffect(() => {
    fetchToken();
    return () => {
      if (renewTimer.current) {
        clearTimeout(renewTimer.current);
      }
    };
  }, []);

  useEffect(() => {
    stateRef.current.tabName = tabName;
    stateRef.current.nominationsGasDay = gasDay;
  }, [tabName, gasDay]);

  useEffect(() => {
    if (url && token) {
      const newConnection = new HubConnectionBuilder()
        .withUrl(url, {
          accessTokenFactory: () => token,
        })
        .withAutomaticReconnect()
        .build();
      setConnection(newConnection);
    }
  }, [url, token]);

  let toastId;
  const handleSyncNominationsRefreshOnClick = (
    event,
    nominationsGasDay: string
  ) => {
    event.stopPropagation();
    if (toastId) {
      toast.dismiss(toastId);
      toastId = '';
    }
    dispatch(getNominations(nominationsGasDay));
  };

  const handleNominationNotifications = (info: IBroadcast) => {
    if (
      info.SubCategory === 'SyncNomination' &&
      stateRef.current.tabName === 'nominations' &&
      stateRef.current.nominationsGasDay === info.GasDay
    ) {
      toastId = toast(
        <ToastComp
          message={typeof info.Message === 'string' ? info.Message : ''}
          onRefresh={(event) =>
            handleSyncNominationsRefreshOnClick(event, info.GasDay)
          }
        />,
        { autoClose: 15000 }
      );
    }
    if (info.SubCategory === 'RejectedNomination') {
      dispatch(
        nominationsActions.setRejectedNominationsCount({ count: info.Count })
      );
    }
    if (info.SubCategory === 'StatusNomination') {
      const status = info.Status;
      dispatch(
        dashboardActions.updateDirectLinksStatus({
          nominationStatus: status.NominationStatus,
          entryCapacityStatus: status.EntryCapacityStatus,
          exitCapacityStatus: status.ExitCapacityStatus,
          schedulingStatus: status.SchedulingStatus,
          tradeMatchingStatus: status.TradeMatchingStatus,
        })
      );
    }
  };

  const handleNotificationCategory = (info: IBroadcast) => {
    if (info.SubCategory === 'TimerNotification') {
      dispatch(
        notificationActions.updateNotifications(info.MessageDetailsList)
      );
    }
    if (info.SubCategory === 'UIChangeNotification') {
      dispatch(getActiveNotifications());
    }
  };

  const handleHealthCheckCategory = (info: IBroadcast) => {
    if (info.SubCategory === 'GeminiHealthCheck') {
      dispatch(
        notificationActions.updateGeminiHealthCheck(info.IsHealthy ?? false)
      );
    }
  };

  const handleNotifications = (info: IBroadcast) => {
    if (info.Category === 'Nomination') {
      handleNominationNotifications(info);
    } else if (info.Category === 'Notification') {
      handleNotificationCategory(info);
    } else if (info.Category === 'HealthCheck') {
      handleHealthCheckCategory(info);
    }
  };

  useEffect(() => {
    if (connection) {
      connection
        .start()
        .then(() => {
          connection.on('Genesis', (info) => {
            handleNotifications(info);
          });
        })
        .catch(() => {
          sendNotification({
            type: 'error',
            message: COMMON_MESSAGES.ERROR,
          });
        });

      connection.onreconnected((connectionId) => {
        dispatch(getNominationStatus());
        // eslint-disable-next-line
        console.log('SignalR Reconnected. Connection ID:', connectionId);
      });
      connection.onclose((error) => {
        if (error) {
          // eslint-disable-next-line
          console.log('SignalR connection closed with error:', error);
        } else {
          // eslint-disable-next-line
          console.log('SignalR connection closed.');
        }
      });

      // Cleanup function to disconnect on page unload
      return () => {
        connection.stop();
      };
    }
    return undefined;
  }, [connection]);

  return null;
};

export default SignalR;
